import { SearchRemoveIcon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import * as React from 'react';
import {
  EmptyState,
  ErrorState,
  PermissionDenied,
  Skeleton,
  SkeletonText,
} from '../primitives/states.js';

/**
 * API-agnostic description of a failed load. Apps map their own error type into this
 * (e.g. HTTP 403 → 'forbidden', 404 → 'not_found').
 */
export interface AsyncErrorInfo {
  kind: 'forbidden' | 'not_found' | 'error';
  /** Human message (never secrets or message content). */
  message?: string;
  /** Technical reference shown in small print (status · code, request id). */
  reference?: string;
}

export interface AsyncErrorViewProps {
  error: AsyncErrorInfo;
  onRetry?: () => void;
  retrying?: boolean;
  /** Role label shown in the permission-denied message (e.g. "Administrador"). */
  requiredRole?: string;
  /** Title for not-found (resource missing or not accessible). */
  notFoundTitle?: string;
  errorTitle?: string;
  compact?: boolean;
}

/** Maps a load failure to the right state: permission denied, not found or error with retry. */
export function AsyncErrorView({
  error,
  onRetry,
  retrying,
  requiredRole,
  notFoundTitle,
  errorTitle,
  compact,
}: AsyncErrorViewProps) {
  if (error.kind === 'forbidden') {
    return (
      <PermissionDenied
        compact={compact}
        requiredRole={requiredRole}
        description={requiredRole ? undefined : error.message}
      />
    );
  }
  if (error.kind === 'not_found') {
    return (
      <EmptyState
        compact={compact}
        icon={<Icon icon={SearchRemoveIcon} aria-hidden />}
        title={notFoundTitle ?? 'No encontramos esta información'}
        description="Puede que no exista, que no tengas acceso o que esta función aún no esté disponible en el servidor."
      />
    );
  }
  return (
    <ErrorState
      compact={compact}
      title={errorTitle}
      description={error.message}
      reference={error.reference}
      onRetry={onRetry}
      retrying={retrying}
    />
  );
}

export type AsyncStatus = 'pending' | 'error' | 'success';

export interface AsyncStateProps<T> extends Omit<AsyncErrorViewProps, 'error'> {
  status: AsyncStatus;
  data: T | undefined;
  error?: AsyncErrorInfo | null;
  children: (data: T) => React.ReactNode;
  /** Loading placeholder (defaults to a text skeleton). */
  loading?: React.ReactNode;
  /** When it returns true, `empty` is rendered instead of children. */
  isEmpty?: (data: T) => boolean;
  empty?: React.ReactNode;
}

/** Renders loading / permission-denied / not-found / error-with-retry / empty around async data, then the data. */
export function AsyncState<T>({
  status,
  data,
  error,
  children,
  loading,
  isEmpty,
  empty,
  ...errorProps
}: AsyncStateProps<T>) {
  if (status === 'pending') return <>{loading ?? <SkeletonText lines={4} />}</>;
  if (status === 'error')
    return (
      <AsyncErrorView error={error ?? { kind: 'error' }} {...errorProps} />
    );
  const value = data as T;
  if (empty !== undefined && isEmpty?.(value)) return <>{empty}</>;
  return <>{children(value)}</>;
}

/** Generic page-level skeleton: header line + blocks. */
export function PageSkeleton({ blocks = 3 }: { blocks?: number }) {
  return (
    <div role="status" aria-label="Cargando…" className="grid gap-4">
      <Skeleton className="h-8 w-64 max-w-full" />
      <Skeleton className="h-4 w-96 max-w-full" />
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {Array.from({ length: blocks }, (_, i) => (
          <Skeleton key={i} className="h-36 w-full rounded-card" />
        ))}
      </div>
    </div>
  );
}
